import { AuxiliaryDataBundle } from '@core/models/entities/entity-visualization-interface';
import { EntityCfgOverrides } from '@core/models/entities/generic-entity-cfg';
import { CustomData } from '@core/models/entities/implementations/custom-data';
import { UserSiteV1Model } from '@core/models/entities/implementations/user-site-v1';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';
import { PolylineFormat, decodePath } from '@core/utils/geo-polyline.utils';

import { GaliaMarker } from '@features/components/visualization/mapbox/models/galia-marker';
import { GaliaRoute } from '@features/components/visualization/mapbox/models/galia-route';
import { GeoLocation } from '@features/components/visualization/mapbox/models/map-data';

export interface UserGeoArcV1Model {
  uid: string;
  name: string | null;
  tail_site_uid: string;
  head_site_uid: string;
  nominal_duration: string | null;
  nominal_distance_meters: number | null;
  vehicle_uids: string[];
  polyline: string | null;
  polyline_format: PolylineFormat;
  custom_data: CustomData;
}

export const ARC_COLOR = '#0f6b73';
export const ARC_NO_GEOMETRY_COLOR = '#8aa2a6';

const ARC_WIDTH = 4;
const NO_GEOMETRY_WIDTH = 2;

function siteCoords(
  uid: string,
  auxData: AuxiliaryDataBundle | undefined
): GeoLocation | null {
  const sites = (auxData?.entitiesArrayPerVersionName['UserSite'] ??
    []) as UserSiteV1Model[];
  const site = sites.find((candidate) => candidate?.uid === uid);
  return site?.coords ?? null;
}

export function buildGeoArcMarkers(
  raw: UserGeoArcV1Model,
  auxData?: AuxiliaryDataBundle
): GaliaMarker[] {
  const endpoints: Array<[string, 'start' | 'finish']> = [
    [raw.tail_site_uid, 'start'],
    [raw.head_site_uid, 'finish']
  ];

  return endpoints.flatMap(([uid, type]) => {
    const coords = siteCoords(uid, auxData);
    // An unresolved site drops its marker; a marker at (0, 0) would read as data.
    if (!coords) {
      return [];
    }
    return [
      {
        uid: `${raw.uid}-${type}`,
        coords,
        title: uid,
        type,
        color: ARC_COLOR,
        description: { name: uid, content: [] }
      } as GaliaMarker
    ];
  });
}

export function buildGeoArcRoutes(
  raw: UserGeoArcV1Model,
  _markers: GaliaMarker[],
  auxData?: AuxiliaryDataBundle
): GaliaRoute[] {
  const path = decodePath(raw.polyline, raw.polyline_format);

  // A single decoded point is not a line: turf.lineString throws on it, which would abort
  // the whole route render. Such an arc falls back to the endpoint-joining placeholder.
  if (path.length >= 2) {
    return [
      {
        uid: raw.uid,
        name: raw.name ?? raw.uid,
        coords: path,
        color: ARC_COLOR,
        width: ARC_WIDTH
      }
    ];
  }

  const tail = siteCoords(raw.tail_site_uid, auxData);
  const head = siteCoords(raw.head_site_uid, auxData);
  if (!tail || !head) {
    return [];
  }

  return [
    {
      uid: raw.uid,
      name: raw.name ?? raw.uid,
      coords: [tail, head],
      color: ARC_NO_GEOMETRY_COLOR,
      width: NO_GEOMETRY_WIDTH,
      dashed: true
    }
  ];
}

export function registerGeoArcExtensions(registry: GenericEntityRegistryService): void {
  const overrides: EntityCfgOverrides<UserGeoArcV1Model> = {
    customVisualizations: new Set(['map'])
  };
  registry.registerOverrides<UserGeoArcV1Model>('UserGeoArc', overrides);
  registry.registerVisualizationBuilders<UserGeoArcV1Model>('UserGeoArc', {
    buildMarkers: buildGeoArcMarkers,
    buildRoutes: buildGeoArcRoutes
  });
}
